export const OFFERING_USE_CASE_OPTIONS = [
  { value: 'generic_offering', label: 'Generic offering' },
  { value: 'food_truck_vendor', label: 'Food truck / vendor' },
  { value: 'facebook_seller', label: 'Facebook seller' },
  { value: 'restaurant_business', label: 'Restaurant / business' },
  { value: 'product_seller', label: 'Product seller' },
];

export const OFFERING_SOURCE_OPTIONS = [
  { value: 'manual_concierge', label: 'Manual concierge' },
  { value: 'facebook_dm', label: 'Facebook DM' },
  { value: 'hip_camden', label: 'Hip Camden' },
  { value: 'food_truck', label: 'Food truck' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'restaurant_referral', label: 'Restaurant referral' },
  { value: 'business_outreach', label: 'Business outreach' },
  { value: 'phone_call', label: 'Phone call' },
];

export const OFFERING_PRESETS = [
  {
    key: 'food_truck_special',
    name: 'Food truck special',
    summary: 'Single local food special with seller review.',
    source: 'food_truck',
    src: 'food_truck',
    sourceLabel: 'Food truck special',
    useCase: 'food_truck_vendor',
    offeringType: 'product',
    category: 'Food',
    title: 'Local food special',
    description: 'Local food special available nearby.',
    price: '',
    quantityAvailable: 1,
    sellerFacingNotes:
      'Review the item, price, quantity, pickup details, and add a clear photo before publishing.',
  },
  {
    key: 'facebook_item',
    name: 'Facebook item',
    summary: 'Item already posted socially; package it as a rich-share offer.',
    source: 'facebook_dm',
    src: 'hip_camden',
    sourceLabel: 'Facebook seller',
    useCase: 'facebook_seller',
    offeringType: 'product',
    category: 'Local goods',
    title: '',
    description: '',
    price: '',
    quantityAvailable: 1,
    sellerFacingNotes:
      'Review the title, price, quantity, and photo before publishing.',
  },
  {
    key: 'local_service',
    name: 'Local service',
    summary: 'Service provider offer with seller approval.',
    source: 'business_outreach',
    src: 'business_outreach',
    sourceLabel: 'Local service',
    useCase: 'restaurant_business',
    offeringType: 'service',
    category: 'Services',
    title: '',
    description: '',
    price: '',
    quantityAvailable: '',
    sellerFacingNotes:
      'Review the service details, price, and where customers should request help before publishing.',
  },
  {
    key: 'custom_offering',
    name: 'Custom offering',
    summary: 'Start with a blank seller-reviewed offering draft.',
    source: 'manual_concierge',
    src: 'manual_concierge',
    sourceLabel: '',
    useCase: 'generic_offering',
    offeringType: 'product',
    category: '',
    title: '',
    description: '',
    price: '',
    quantityAvailable: 1,
    sellerFacingNotes:
      'Review and adjust this offering before publishing. Add a clear photo for better rich-share performance.',
  },
];

export const OFFERING_SCRIPT_CARDS = [
  {
    tab: 'Food Truck / Vendor',
    title: 'Food truck / vendor',
    text:
      'We can help you create a free JG LocalOps offering for one lunch special. It shows up on JG LocalOps, gets shared through our local system, and gives you a rich-share link with the photo, price, and details packaged together. You can share that link on Facebook, Hip Camden, Marketplace, or text, and people can click from their phone and request it.',
  },
  {
    tab: 'Facebook Seller',
    title: 'Already posts on Facebook',
    text:
      "That's perfect. JG LocalOps gives you a rich-share ordering/request link to add to your Facebook post. So instead of people only seeing what you have, they can click, see the photo, price, and details, and request it right from their phone.",
  },
  {
    tab: 'Objections',
    title: 'Cost question',
    text:
      'Creating the offering is free right now. The goal is to help local vendors test whether people respond to the rich-share offering.',
  },
  {
    tab: 'Restaurant / Business',
    title: 'Pickup/delivery question',
    text:
      'If someone reserves the offering and needs pickup help, LocalOps has a pickup/errand task flow. A local helper can potentially be paid to pick it up and bring it to them.',
  },
  {
    tab: 'Objections',
    title: 'DoorDash comparison',
    text:
      "Not exactly. This is more local and broader. It can be food offerings, local products, errands, pickups, delivery help, yard help, and small jobs. The idea is Camden people helping Camden people.",
  },
  {
    tab: 'Objections',
    title: 'Skeptical seller',
    text:
      "No pressure at all. The easiest way to test it is one item, one link, one post. If people respond, great. If not, you haven't changed your whole business.",
  },
  {
    tab: 'Close',
    title: 'Close',
    text:
      'Would you be open to creating one free offering for a lunch special right now?',
  },
  {
    tab: 'Close',
    title: 'Busy vendor',
    text:
      'No problem. What is the best email or phone so we can help you set it up later?',
  },
];

export const trimOfferingValue = (value) => String(value || '').trim();

export const getOfferingPreset = (presetKey) =>
  OFFERING_PRESETS.find((preset) => preset.key === presetKey) ||
  OFFERING_PRESETS.find((preset) => preset.key === 'custom_offering');

export const buildDefaultOfferingConciergeForm = () => ({
  presetKey: 'custom_offering',
  sellerEmail: '',
  sellerName: '',
  sellerPhone: '',
  source: 'manual_concierge',
  src: 'manual_concierge',
  sourceLabel: '',
  useCase: 'generic_offering',
  offeringType: 'product',
  category: '',
  title: '',
  description: '',
  price: '',
  quantityAvailable: '1',
  pickupAddress: '',
  sellerFacingNotes:
    'Review and adjust this offering before publishing. Add a clear photo for better rich-share performance.',
  internalNotes: '',
  originalRequest: '',
});

export const applyOfferingPresetToForm = (form, presetKey) => {
  const preset = getOfferingPreset(presetKey);
  return {
    ...form,
    presetKey: preset.key,
    source: preset.source,
    src: preset.src || preset.source,
    sourceLabel: preset.sourceLabel,
    useCase: preset.useCase,
    offeringType: preset.offeringType,
    category: preset.category,
    title: preset.title,
    description: preset.description,
    price: preset.price,
    quantityAvailable: preset.quantityAvailable === '' ? '' : String(preset.quantityAvailable),
    sellerFacingNotes: preset.sellerFacingNotes,
  };
};

export const buildOfferingDraftPayload = (form) => ({
  sellerEmail: form.sellerEmail,
  sellerName: form.sellerName,
  sellerPhone: form.sellerPhone,
  source: form.source,
  src: form.src,
  sourceLabel: form.sourceLabel,
  useCase: form.useCase,
  offeringType: form.offeringType,
  category: form.category,
  title: form.title,
  description: form.description,
  price: Number(form.price),
  quantityAvailable:
    form.offeringType === 'service' && !trimOfferingValue(form.quantityAvailable)
      ? undefined
      : Number(form.quantityAvailable),
  pickupAddress: form.pickupAddress,
  sellerFacingNotes: form.sellerFacingNotes,
  internalNotes: form.internalNotes,
  originalRequest: form.originalRequest,
  regionSlug: 'camden',
  marketSlug: 'camden',
  intent: 'post_offering',
});
